/* eslint-disable no-tabs */
import { Notification } from "element-ui";
import { playSoundSuccess, playSoundFail } from "@/api/utils/audio.js";

/**
 * 获取当前全屏容器元素。
 * 若处于全屏状态，返回全屏元素；否则返回 null。
 */
function getFullscreenContainer() {
  return (
    document.fullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitFullscreenElement ||
    null
  );
}

/**
 * 调用 Notification 后，若当前处于全屏状态，
 * 将最后一个挂载在 body 上的 notification DOM 节点移入全屏容器，
 * 使其在全屏模式下可见。
 */
export function moveNotifyToFullscreen() {
  const fullscreenEl = getFullscreenContainer();
  if (!fullscreenEl) return;
  // Element UI 的 Notification DOM 挂载在 body 最后，类名为 el-notification
  const notifyEls = document.querySelectorAll("body > .el-notification");
  if (!notifyEls.length) return;
  const last = notifyEls[notifyEls.length - 1];
  fullscreenEl.appendChild(last);
}

/**
 * NOTIFY_TYPE_ENUM 消息提示类型枚举
 * success 成功
 * info 提示
 * warning 警告
 * error： 错误
 * */

export const NOTIFY_TYPE_ENUM = {
  success: "success",
  info: "info",
  warning: "warning",
  error: "error"
};

// 用计数器而非布尔，支持嵌套的扫码抑制（内层 end 不会提前关闭外层抑制）
let _suppressCount = 0
let _lastSuppressedMsg = ''
export function setNotifySuppressed(val) {
  if (val) {
    _suppressCount++
    // 开启抑制时清空上次暂存，便于本次扫码取用
    _lastSuppressedMsg = ''
  } else {
    _suppressCount = Math.max(0, _suppressCount - 1)
  }
}
// 取出抑制期间最后一条被拦截的提示文案（取后清空，避免嵌套时重复展示）
export function getLastSuppressedMsg() {
  const m = _lastSuppressedMsg
  _lastSuppressedMsg = ''
  return m
}

export const TIPS_TYPE_OBJECT = {
  "32N": {
    rule: "OchamaCarton"
  },
  "31N": {
    rule: "GrayPlastic"
  },
  "31C": {
    rule: "TransparentPlastic"
  },
  "31F": {
    rule: "InsulationBag"
  },
  "92N": {
    rule: "SelfPickupPlastic"
  },
  "92C": {
    rule: "TransparentPlasticBag"
  },
  "92F": {
    rule: "FoamBox"
  },
  "91N": {
    rule: "SelfPickupPlastic"
  },
  "91C": {
    rule: "FoamBoxBlue"
  },
  "91F": {
    rule: "FoamBox"
  },
  "102N": {
    rule: "OchamaCarton"
  },
  "112N": {
    rule: "GrayPlastic"
  },
  "112C": {
    rule: "TransparentPlasticBag"
  },
  "112F": {
    rule: "TransparentPlasticBag"
  },
  "122N": {
    rule: "OchamaCarton"
  },
  "132N": {
    rule: "GrayPlasticOchamaCarton"
  },
  "142N": {
    rule: "OchamaCarton"
  },
  "142F": {
    rule: "FoamBox"
  },
  "142C": {
    rule: "FoamBoxBlue"
  }
};
/**
 * 此方法支持普通提示调用， 接口返回提示调用, 带提示音
 * 自定义HTML模板成功提示
 * dangerouslyUseHTMLString 是否将 message 属性作为 HTML 片段处理  默认false
 * duration 显示时间, 毫秒。设为 0 则不会自动关闭 默认4500
 * type 类型是必要的 success 成功提示 info 信息提示 warning 警告提示 error 错误提示
 * error 类型会中断当前线程
 * message 需要提示的消息
 * config 接口返回值在 error类型下传，非必填
 */
export function customizeNotifyWithVoice(type, message, config) {
  if (_suppressCount === 0) {
    Notification({
      type: type,
      dangerouslyUseHTMLString: true,
      duration: 8000,
      message: messageFormat(message)
    });
    moveNotifyToFullscreen();
  } else if (typeof message === "string") {
    _lastSuppressedMsg = message;
  }
  if (type === NOTIFY_TYPE_ENUM.success) {
    playSoundSuccess();
  } else if (type === NOTIFY_TYPE_ENUM.info) {
    playSoundSuccess();
  } else if (type === NOTIFY_TYPE_ENUM.warning) {
    playSoundFail();
  } else if (type === NOTIFY_TYPE_ENUM.error) {
    playSoundFail();
    // 定制化$notify.error $notify.error约定：停止当前线程
    if (config && config.hasOwnProperty("ternimal") && !config.ternimal) {
      return;
    }
    throw new Error(`$notify.error ternimal current thread ${message}`);
  }
}

export function customizeNotifyWithVoiceFormat(type, message, config) {
  if (_suppressCount === 0) {
    Notification({
      type: type,
      dangerouslyUseHTMLString: true,
      duration: 8000,
      message: message
    });
    moveNotifyToFullscreen();
  } else if (typeof message === "string") {
    _lastSuppressedMsg = message;
  }
  if (type === NOTIFY_TYPE_ENUM.success) {
    playSoundSuccess();
  } else if (type === NOTIFY_TYPE_ENUM.info) {
    playSoundSuccess();
  } else if (type === NOTIFY_TYPE_ENUM.warning) {
    playSoundFail();
  } else if (type === NOTIFY_TYPE_ENUM.error) {
    playSoundFail();
    // 定制化$notify.error $notify.error约定：停止当前线程
    if (config && config.hasOwnProperty("ternimal") && !config.ternimal) {
      return;
    }
    throw new Error(`$notify.error ternimal current thread ${message}`);
  }
}

/**
 * 此方法支持普通提示调用， 接口返回提示调用，无提示音
 * 自定义HTML模板成功提示
 * dangerouslyUseHTMLString 是否将 message 属性作为 HTML 片段处理  默认false
 * duration 显示时间, 毫秒。设为 0 则不会自动关闭 默认4500
 * type 类型是必要的 success 成功提示 info 信息提示 warning 警告提示 error 错误提示
 * error 类型会中断当前线程
 * message 需要提示的消息
 * config 接口返回值在 error类型下传，非必填
 */
export function customizeNotify(type, message, config) {
  if (_suppressCount === 0) {
    Notification({
      type: type,
      dangerouslyUseHTMLString: true,
      duration: 8000,
      message: messageFormat(message)
    });
    moveNotifyToFullscreen();
  } else if (typeof message === "string") {
    _lastSuppressedMsg = message;
  }
  if (type === NOTIFY_TYPE_ENUM.error) {
    playSoundFail();
    // 定制化$notify.error $notify.error约定：停止当前线程
    if (config && config.hasOwnProperty("ternimal") && !config.ternimal) {
      return;
    }
    throw new Error(`$notify.error ternimal current thread ${message}`);
  }
}

// 提示信息格式化
function messageFormat(message) {
  return (
    '<div style="font-size:20px;width:230px;word-break:break-all;"><strong><span>' +
    message +
    "</span></strong></div>"
  );
}
